import React from 'react';
import {
  Button,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { jurisdictionOptions } from '../../../../data/contactCenter/aurora/dropDownOptions';
import { canAccessJurisdiction } from '../helpers/jurisdictionSkills';

export default function AuroraSearchForm({
  jurisdiction,
  retailerNumber,
  setRetailerNumber,
  onSearch,
  onJurisdictionChange,
  onClear,
  searchLoading,
  isAuroraAdmin,
  skillsLoading,
  retailerSkills,
  csSkills,
  translations,
}) {
  return (
    <CardContent>
      <Stack alignItems='center' spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ width: '100%', maxWidth: 720 }}
        >
          <FormControl
            fullWidth
            size='small'
            sx={{
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: 'var(--canopus-violet)',
                },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--canopus-violet)',
              },
              minWidth: { xs: '100%', sm: 260 },
              flex: 1,
            }}
          >
            <InputLabel>
              {translations.selectJurisdiction || 'Select Jurisdiction'}
            </InputLabel>
            <Select
              value={jurisdiction}
              label={translations.selectJurisdiction || 'Select Jurisdiction'}
              onChange={e => {
                const nextJurisdiction = e.target.value;
                onJurisdictionChange?.(nextJurisdiction);
              }}
              name='jurisdiction'
              disabled={!isAuroraAdmin && skillsLoading}
            >
              {jurisdictionOptions.map(jid => {
                const hasAccess =
                  isAuroraAdmin ||
                  canAccessJurisdiction(jid, retailerSkills, csSkills);
                return (
                  <MenuItem
                    key={jid}
                    value={jid}
                    disabled={!hasAccess}
                    sx={
                      hasAccess
                        ? undefined
                        : {
                            color: 'text.disabled',
                            opacity: 0.72,
                          }
                    }
                  >
                    {jid}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size='small'
            type='text'
            label={translations.enterRetailerNumber || 'Enter Retailer Number'}
            value={retailerNumber}
            disabled={jurisdiction === ''}
            onChange={e => setRetailerNumber(e.target.value)}
            variant='outlined'
            sx={{
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: 'var(--canopus-violet)',
                },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--canopus-violet)',
              },
              flex: 1,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onSearch();
              }
            }}
          />
        </Stack>

        <Stack direction='row' spacing={1} justifyContent='center'>
          <Button
            onClick={onSearch}
            variant='contained'
            disabled={
              jurisdiction === '' ||
              retailerNumber.trim() === '' ||
              searchLoading
            }
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              minWidth: 140,
              color: '#FFFFFF',
              background:
                'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
              boxShadow:
                '0 2px 8px rgba(5, 5, 51, 0.08), 0 6px 20px rgba(5, 5, 51, 0.07)',
              '&:hover': {
                background:
                  'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
                filter: 'brightness(1.08)',
                boxShadow:
                  '0 4px 12px rgba(5, 5, 51, 0.1), 0 12px 28px rgba(5, 5, 51, 0.12)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.65)',
                background:
                  'linear-gradient(90deg, #050A30 0%, #0B1F4D 55%, #143A7B 100%)',
                opacity: 0.6,
              },
            }}
          >
            {searchLoading
              ? translations.searching || 'Searching…'
              : translations.search || 'Search'}
          </Button>
          <Button
            variant='outlined'
            disabled={searchLoading && retailerNumber.trim() !== ''}
            onClick={onClear}
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              borderColor: '#050A30',
              color: '#050A30',
              minWidth: 140,
              '&:hover': {
                borderColor: '#050A30',
                backgroundColor: 'rgba(5, 10, 48, 0.04)',
              },
            }}
          >
            {translations.clear || 'Clear'}
          </Button>
        </Stack>
      </Stack>
    </CardContent>
  );
}
